import React, { useEffect, useState, useCallback, useRef } from "react";
import { getSettingsSchema, updateSettings, SettingDefinition } from "../../../services/adminSettingsApi";
import { logger } from "../../../services/logger";
import styles from "../TradeFlowTrackingTab.module.css";

const SETTINGS_BY_TAB: Record<number, string[]> = {
  1: ["flow.platform_fetch.enabled","flow.platform_fetch.max_pairs","flow.platform_fetch.rank_by"],
  2: ["flow.genesis.enabled","flow.genesis.min_volume","flow.genesis.min_volatility_pct","flow.genesis.min_volatility_1h_pct","flow.genesis.max_spread_pct","trading.minimum_coin_price"],
  3: ["trading.exclude_flat_structure","trading.flat_structure_min_change_pct","trading.enable_direction_filter","trading.direction_min_change_1h","trading.enable_peak_avoidance_filter","trading.peak_avoidance_max_change_1h","trading.enable_movement_quality_filter","trading.movement_quality_vol_spread_mult","trading.enable_not_stable_filter","trading.enable_volume_support_filter","trading.enable_smart_direction_mode","genius_filter.enabled","genius_filter.r1_enabled","genius_filter.r1_gap_enabled","genius_filter.macro_bear_penalty","genius_filter.macro_strong_bear_penalty","genius_filter.nota_threshold","vwap.enabled","session_filter.enabled","regime.enabled","regime.allow_trending","regime.allow_ranging","regime.allow_volatile","regime.allow_chop","composite.chop_block_enabled","mtf.brain_signal_profile"],
  4: ["gate.xgboost_enabled","gate.xgboost_threshold","gate.xgboost_mode","gate.xgboost_prefilter_liquidity","gate.xgboost_prefilter_spread","gate.xgboost_prefilter_min_volatility","gate.xgboost_prefilter_flat_structure","gate.xgboost_prefilter_price_compression","gate.xgboost_prefilter_direction","gate.xgboost_prefilter_peak_avoidance","gate.xgboost_prefilter_movement_quality","gate.xgboost_prefilter_not_stable","gate.xgboost_prefilter_volume_support","gate.xgboost_prefilter_structure_position","gate.xgboost_prefilter_v4_enabled","gate.xgboost_prefilter_v4_uptrend","gate.xgboost_prefilter_v4_rsi_momentum","gate.xgboost_prefilter_v4_non_decay","gate.xgboost_prefilter_v4_volatility","gate.xgboost_prefilter_v7_enabled","gate.xgboost_prefilter_v7_rsi_min","gate.xgboost_prefilter_v7_trend_enabled","gate.xgboost_prefilter_v7_dist_ema50_enabled","gate.xgboost_prefilter_v7_dist_ema50_min"],
  5: ["gate.lgb_enabled","gate.lgb_threshold","gate.lgb_mode","gate.lgb_prefilter_liquidity","gate.lgb_prefilter_spread","gate.lgb_prefilter_min_volatility","gate.lgb_prefilter_flat_structure","gate.lgb_prefilter_price_compression","gate.lgb_prefilter_direction","gate.lgb_prefilter_peak_avoidance","gate.lgb_prefilter_movement_quality","gate.lgb_prefilter_not_stable","gate.lgb_prefilter_volume_support","gate.lgb_prefilter_structure_position"],
  6: ["pool.min_hybrid_score","pool.micro_score_weight","pool.rr_bonus_max","queue.score_weight.setup_quality","queue.score_weight.momentum_readiness","queue.score_weight.anti_chop","queue.score_weight.time_to_target","queue.score_weight.execution_freshness","queue.score_weight.volume_context","queue.score_weight.tp_headroom","scoring.technical_weight","scoring.volatility_weight","scoring.liquidity_weight","scoring.sentiment_weight","scoring.structure_weight","scoring.top_context_weight","scoring.top_timing_weight","scoring.top_quality_weight","scoring.rsi_sweet_spot_min","scoring.rsi_sweet_spot_max"],
  7: ["queue.refresh_interval_seconds","queue.max_age_minutes","queue.invalidate_on_trend_misalignment","gate.active_pool_min_score","gate.smart_pool_min_confidence","ranking.confidence_weight","ranking.volume_weight","ranking.trend_alignment_weight","ranking.tp_headroom_weight","mtf.refresh_profile"],
  8: ["gate.enabled","gate.failure_window_seconds","gate.cooldown_seconds","gate.failure_threshold","gate.score_threshold_enabled","gate.timing_score_min","gate.context_score_min","gate.min_tp_headroom_percent","gate.filter.soft_vol_break.enabled","gate.filter.bearish_close.enabled","gate.filter.anti_peak_fomo.enabled","gate.filter.soft_delay.enabled","gate.filter.near_resistance.enabled","gate.filter.vwap_alignment.enabled","gate.filter.volume_context.enabled","gate.filter.mid_range.enabled","gate.filter.weak_breakout.enabled","gate.counter_signal_detection","gate.signal_direction_check","gate.soft_vol_break_mode","gate.soft_vol_multiplier","gate.anti_peak_distance_pct","gate.stale_drift_threshold","gate.defer.snooze_ttl_seconds","gate.defer.max_count","gate.defer.max_wait_seconds","volume_context.enabled","volume_context.min_volume_spike_ratio","volume_context.obv_confirmation_required","volume_context.cmf_threshold"],
  9: ["gate.technical_required_count","gate.rsi_enabled","gate.macd_enabled","gate.ema_enabled","gate.bollinger_enabled","gate.adx_enabled","gate.cci_mfi_enabled","gate.cci_overbought_threshold","gate.cci_oversold_threshold","gate.mfi_overbought_threshold","gate.mfi_oversold_threshold","gate.rsi_buy_zone_low","gate.rsi_buy_zone_high","scoring.indicator.rsi.enabled","scoring.indicator.macd.enabled","scoring.indicator.ema_cross.enabled","scoring.indicator.bollinger.enabled","scoring.indicator.volume.enabled","scoring.indicator.atr.enabled","scoring.indicator.stochastic.enabled","scoring.indicator.adx.enabled","scoring.indicator.rsi.weight","scoring.indicator.macd.weight","scoring.indicator.ema_cross.weight","scoring.indicator.bollinger.weight","scoring.indicator.volume.weight","scoring.indicator.atr.weight","scoring.indicator.stochastic.weight","scoring.indicator.adx.weight","indicator.rsi.period","indicator.rsi.overbought","indicator.rsi.oversold","indicator.macd.fast_period","indicator.macd.slow_period","indicator.macd.signal_period","indicator.ema_cross.fast_period","indicator.ema_cross.slow_period","indicator.bollinger.period","indicator.bollinger.std_dev","indicator.bollinger.squeeze_threshold","indicator.volume.sma_period","mtf.live_gate_profile"],
  10: ["sniper.enabled","sniper.window_seconds","sniper.poll_interval_ms","sniper.min_score_safe","sniper.min_score_aggressive","sniper.max_adverse_move_pct","sniper.adaptive_window_enabled","sniper.adaptive_window_min_seconds","sniper.adaptive_window_max_seconds"],
};

const TAB_LABELS: Record<number, string> = {
  1: "Platform Fetch Controls",
  2: "Genesis Filter Thresholds",
  3: "Pipeline Filter Controls",
  4: "XGBoost AI Settings",
  5: "LightGBM AI Settings",
  6: "Scoring & Pool Entry Gates",
  7: "Active/Reserve Pool Controls",
  8: "Live Entry Gate Controls",
  9: "Technical Indicator Settings",
  10: "Smart Entry Sniper Settings",
};
interface SettingsPanelProps {
  tabNumber: number;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ tabNumber }) => {
  const [defs, setDefs] = useState<SettingDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirtyValues, setDirtyValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const fetchedRef = useRef(false);
  const tabRef = useRef(tabNumber);

  const keys = SETTINGS_BY_TAB[tabNumber];
  const label = TAB_LABELS[tabNumber] || "Settings";

  useEffect(() => {
    if (!keys || keys.length === 0) {
      setLoading(false);
      setDefs([]);
      return;
    }

    tabRef.current = tabNumber;
    fetchedRef.current = true;
    setLoading(true);
    setError(null);
    setDirtyValues({});
    setSaveSuccess(false);

    const keysSet = new Set(keys);

    getSettingsSchema()
      .then((schema) => {
        if (tabRef.current !== tabNumber) {
          return;
        }
        const matched: SettingDefinition[] = [];
        for (const section of Object.values(schema)) {
          for (const subsection of Object.values(section)) {
            if (!Array.isArray(subsection)) {
              continue;
            }
            for (const def of subsection) {
              if (keysSet.has(def.key)) {
                matched.push(def);
              }
            }
          }
        }
        setDefs(matched);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (tabRef.current !== tabNumber) {
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        logger.error("SettingsPanel: failed to fetch settings schema", err instanceof Error ? err : new Error(msg));
        setError(msg);
        setLoading(false);
      });
  }, [tabNumber, keys]);

  const handleChange = useCallback((key: string, value: unknown) => {
    setDirtyValues((prev) => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
  }, []);

  const handleSave = useCallback(async () => {
    const updates = Object.entries(dirtyValues).map(([key, value]) => ({
      key,
      value,
    }));
    if (updates.length === 0) {
      return;
    }
    setSaving(true);
    try {
      await updateSettings({ updates });
      setDirtyValues({});
      setSaveSuccess(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      logger.error("SettingsPanel: failed to save settings", err instanceof Error ? err : new Error(msg));
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [dirtyValues]);

  const dirtyCount = Object.keys(dirtyValues).length;

  if (!keys || keys.length === 0) {
    return (
      <div className={styles.settingsPanel}>
        <div className={styles.settingsPanelHeader}>
          <h3>Settings</h3>
        </div>
        <div className={styles.settingsPanelEmpty}>
          No settings available for this stage.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settingsPanel}>
      <div className={styles.settingsPanelHeader}>
        <h3>{label}</h3>
        <span className={styles.settingsPanelSubtitle}>
          {defs.length} controls &middot; references same settings as Entry & Filter tabs
        </span>
      </div>

      <div className={styles.settingsPanelBody}>
        {loading && (
          <div className={styles.settingsLoading}>Loading settings...</div>
        )}

        {error && (
          <div className={styles.settingsError}>
            {error}
            <button
              className={styles.settingsRetryBtn}
              onClick={() => {
                setError(null);
                setLoading(true);
                getSettingsSchema()
                  .then((schema) => {
                    const keysSet = new Set(keys);
                    const matched: SettingDefinition[] = [];
                    for (const section of Object.values(schema)) {
                      for (const subsection of Object.values(section)) {
                        if (!Array.isArray(subsection)) {
                          continue;
                        }
                        for (const def of subsection) {
                          if (keysSet.has(def.key)) {
                            matched.push(def);
                          }
                        }
                      }
                    }
                    setDefs(matched);
                    setLoading(false);
                  })
                  .catch((err: unknown) => {
                    const msg = err instanceof Error ? err.message : String(err);
                    setError(msg);
                    setLoading(false);
                  });
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && defs.length === 0 && (
          <div className={styles.settingsPanelEmpty}>
            No matching settings found. They may not be registered yet.
          </div>
        )}

        {!loading && !error && defs.length > 0 && (
          <>
            <div className={styles.settingsControlsList}>
              {defs.map((def) => (
                <SettingControl
                  key={def.key}
                  definition={def}
                  dirtyValue={dirtyValues[def.key]}
                  onChange={handleChange}
                />
              ))}
            </div>

            <div className={styles.settingsActions}>
              {dirtyCount > 0 && (
                <span className={styles.settingsDirtyHint}>
                  {dirtyCount} unsaved change{dirtyCount !== 1 ? "s" : ""}
                </span>
              )}
              {saveSuccess && (
                <span className={styles.settingsSavedHint}>Saved</span>
              )}
              <button
                className={styles.settingsSaveBtn}
                disabled={dirtyCount === 0 || saving}
                onClick={handleSave}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface SettingControlProps {
  definition: SettingDefinition;
  dirtyValue: unknown;
  onChange: (key: string, value: unknown) => void;
}

const SettingControl: React.FC<SettingControlProps> = ({
  definition,
  dirtyValue,
  onChange,
}) => {
  const { key, label, type, current_value, constraints, description, warning_text, is_dangerous } = definition;

  const effectiveValue = dirtyValue !== undefined ? dirtyValue : current_value;

  const inputId = "flow-setting-" + key.replace(/\./g, "-");

  return (
    <div className={styles.settingRow + (is_dangerous ? " " + styles.settingDangerous : "")}>
      <div className={styles.settingInfo}>
        <label htmlFor={inputId} className={styles.settingLabel}>
          {label || key}
        </label>
        {description && (
          <span className={styles.settingDescription}>{description}</span>
        )}
        {warning_text && (
          <span className={styles.settingWarning}>{warning_text}</span>
        )}
      </div>

      <div className={styles.settingInput}>
        {type === "bool" && (
          <label className={styles.toggleSwitch}>
            <input
              type="checkbox"
              id={inputId}
              checked={effectiveValue === true}
              onChange={(e) => onChange(key, e.target.checked)}
            />
            <span className={styles.toggleSlider} />
          </label>
        )}

        {(type === "int" || type === "float") && (
          <div className={styles.numberInputGroup}>
            <input
              type="number"
              id={inputId}
              className={styles.numberInput}
              value={
                effectiveValue !== null && effectiveValue !== undefined
                  ? String(effectiveValue)
                  : ""
              }
              min={constraints?.min}
              max={constraints?.max}
              step={constraints?.step ?? (type === "float" ? 0.01 : 1)}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  onChange(key, null);
                  return;
                }
                const parsed = type === "float" ? parseFloat(raw) : parseInt(raw, 10);
                if (!isNaN(parsed)) {
                  onChange(key, parsed);
                }
              }}
            />
            {constraints && (
              <span className={styles.numberRange}>
                {constraints.min !== undefined ? "Min: " + constraints.min : ""}
                {constraints.min !== undefined && constraints.max !== undefined ? " / " : ""}
                {constraints.max !== undefined ? "Max: " + constraints.max : ""}
              </span>
            )}
          </div>
        )}

        {(type === "string" || type === "select") && (
          <div className={styles.textInputGroup}>
            {type === "select" && constraints?.options ? (
              <select
                id={inputId}
                className={styles.selectInput}
                value={String(effectiveValue ?? "")}
                onChange={(e) => onChange(key, e.target.value)}
              >
                {constraints.options.map((opt) => {
                  const val = typeof opt === "object" && opt !== null ? (opt as { value: string | number }).value : opt;
                  const lbl = typeof opt === "object" && opt !== null ? (opt as { label: string }).label : String(opt);
                  return (
                    <option key={String(val)} value={String(val)}>
                      {lbl}
                    </option>
                  );
                })}
              </select>
            ) : (
              <input
                type="text"
                id={inputId}
                className={styles.textInput}
                value={String(effectiveValue ?? "")}
                onChange={(e) => onChange(key, e.target.value)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPanel;